import { updateOffersBatch } from '../SummaryCaseTable/services/realtimeOfferService.js';
import { usePersistedDataStore } from '../../store';
import { updateCaseStatusGeneralized } from '../SummaryCaseTable/services/caseStatusUpdateService.js';
import { updateCourtfilesGeneralized } from '../SummaryCaseTable/services/courtfilesUpdateService.js';
import { isTownViewDevNoPersist } from '../../../utils/townViewDevMode.js';
import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import { toast } from '../../../common/toast';
import { buildStipulationObject, generateStipsOnSettlement } from './downloadStips.js';
import { getAVUtil, getIFMVUtil } from '../../../utils/getAVUtil';
import { validateAndAdjustOfferValue } from '../../../utils/offerThresholdUtils';
import { findNegotiationCaseByParcelId } from '../../../utils/conversionUtils.js';
import { toMySQLDateTime } from '../../../common/helper_functions.js';
import logger from '../../../utils/logger';
import { negotiatorIdForSettlement } from './negotiatorLookup.js';
import { isMasterPetitionCase } from '../SummaryCaseTable/utils/helpers.js';

const SETTLEMENT_PIPELINE_REVIEW_TYPES = ['DataCleaning', 'CaseReview', 'Negotiation'];

const completeSettlementPipelineTasks = async (parcelIds, context, currentDate) => {
  const errors = [];
  for (const reviewType of SETTLEMENT_PIPELINE_REVIEW_TYPES) {
    try {
      await updateCaseStatusGeneralized({
        muniCode: context.muniCode,
        taxYear: context.taxYear,
        reviewType,
        parcelIds,
        fieldsToUpdate: { assignee: context.userId, DateCompleted: currentDate },
        updateFrontend: true,
        forceUpdateCompleted: true,
        townView: context.townView,
        villageRun: context.villageRun || 0,
      });
    } catch (e) {
      logger.error(`Failed to mark ${reviewType} tasks as completed during settlement:`, e);
      errors.push({ reviewType, error: e.message });
    }
  }
  return { success: errors.length === 0, errors };
};

/**
 * Cap display input: AV = assessment, MV = IFMV. Non-negative. No settlement rounding here.
 * Truncation toward zero (not Math.round on the cap).
 */
export function clampDisplayedSettlement(n, isAVMode, caseObj) {
  if (!Number.isFinite(n)) return n;
  const lo = Math.max(0, Math.trunc(n));
  const max = isAVMode ? getAVUtil(caseObj) : getIFMVUtil(caseObj);
  if (max == null || !Number.isFinite(Number(max))) return lo;
  return Math.min(lo, Number(max));
}

/**
 * SettlementService - Handles case settlement operations
 * Takes a caseObject as parameter and processes settlement logic
 */

/**
 * Multi-settlement validation helper.
 * Given an array of caseObjects (each with settlementValue in AV terms), checks each against the
 * unified offer thresholds (assessment cap, 25% IFMV floor, Master Petition downside skip).
 * Returns an array of violations; caller is responsible for surfacing errors / highlighting.
 *
 * @param {Array} caseObjects - Array of case objects with settlementValue populated (AV terms)
 * @returns {Array<{ parcel_id: string|number, adjustedValueAV: number }>}
 */
export const validateSettlementBatch = (caseObjects) => {
  if (!Array.isArray(caseObjects) || caseObjects.length === 0) return [];

  const violations = [];

  for (let index = 0; index < caseObjects.length; index += 1) {
    const caseObj = caseObjects[index];
    const parcelId = caseObj.parcel_id;
    const settlementValueAV = caseObj.settlementValue;
    logger.log('🔍 [BATCH VALIDATION] start', {
      index,
      parcelId,
      settlementValueAV,
      fullCase: caseObj,
    });
    if (!parcelId) {
      continue;
    }
    if (settlementValueAV == null || !Number.isFinite(Number(settlementValueAV)) || Number(settlementValueAV) <= 0) {
      violations.push({ index, parcel_id: parcelId });
      continue;
    }

    const propertyAssessment = getAVUtil(caseObj) ?? 0;

    // Explicit upside check: never allow settlement AV above Assessment
    const upsideTolerance = Math.max(1, propertyAssessment * 1e-6);
    const exceedsUpside = propertyAssessment > 0 && settlementValueAV > propertyAssessment + upsideTolerance;
    logger.log('🔍 [BATCH VALIDATION] upside check', {
      index,
      parcelId,
      settlementValueAV,
      propertyAssessment,
      upsideTolerance,
      exceeds: exceedsUpside,
    });
    if (exceedsUpside) {
      logger.log('🔍 [BATCH VALIDATION] UPSIDE violation', {
        index,
        parcelId,
        settlementValueAV,
        propertyAssessment,
      });
      violations.push({
        index,
        parcel_id: parcelId,
        adjustedValueAV: propertyAssessment,
      });
      continue;
    }

    const { adjustedValue, wasAdjusted } = validateAndAdjustOfferValue(
      settlementValueAV,
      caseObj,
      true,
      false,
      { settlementStage: caseObj.settlementDetails?.stage }
    );

    logger.log('🔍 [BATCH VALIDATION] unified result', {
      index,
      parcelId,
      settlementValueAV,
      adjustedValue,
      wasAdjusted,
    });
    if (wasAdjusted) {
      violations.push({ index, parcel_id: parcelId, adjustedValueAV: adjustedValue });
    }
  }

  logger.log('🔍 [BATCH VALIDATION] violations summary', violations);
  return violations;
};

/**
 * Determine the appropriate DeterminationAction based on stage and settlement logic
 * @param {string} stage - The stage (SCAR, Art7, or BAR)
 * @param {number} settlementValue - The settlement value (in AV terms)
 * @param {number} originalAssessment - The original assessment value (in AV terms)
 * @param {boolean} isWithdrawal - Whether this is a withdrawal
 * @returns {string} The determination action code
 */
const determineActionCode = (stage, settlementValue, originalAssessment, isWithdrawal = false) => {
  if (isWithdrawal) return stage === 'BAR' ? 'BW' : 'W';
  const percentageChange = Math.abs((settlementValue - originalAssessment) / originalAssessment);
  return percentageChange < 0.0025
    ? (stage === 'BAR' ? 'BD' : 'SD')
    : (stage === 'BAR' ? 'BT' : 'ST');
};

/**
 * Process case settlement (individual or batch) -- this is what actually sets a case to be "Done" - HOWEVER:
 * Stip countersignature and nyscef upload are still required after this stage
 * @param {Object|Array} caseObjectOrArray - Single case object or array of case objects
 * @param {Object} context - Context: muniCode, taxYear, userId, userAccess, villageRun, caseType; optional courtDate (counterparty link scope); optional townView (town portal: issued_by negotiator for courtfiles + offers; skips /generate_stipulations)
 * @returns {Promise<Object>} Settlement result
 * @remarks Town portal: keep optimistic row UI (e.g. Town Response settled chip) in the caller around this promise; it rejects on failure and resolves on success after store updates.
 */
export const processSettlement = async (caseObjectOrArray, context) => {
  try {
    // Normalize input to always be an array
    const passedAsArray = Array.isArray(caseObjectOrArray);
    const caseObjects = passedAsArray ? caseObjectOrArray : [caseObjectOrArray];
    
    logger.log('Processing settlement for case(s):', caseObjects.length, context);
    logger.log('Case objects:', caseObjects);
    
    // Validate inputs
    if (!Array.isArray(caseObjects) || caseObjects.length === 0) {
      throw new Error('Invalid case objects: must be non-empty array');
    }
    
    if (!context || !context.muniCode || !context.taxYear || !context.userId) {
      throw new Error('Missing required context: muniCode, taxYear, or userId');
    }
    
    // Extract parcel IDs for stipulation
    const parcelIds = caseObjects.map(caseObj => caseObj.parcel_id).filter(Boolean);
    
    if (parcelIds.length === 0) {
      throw new Error('No valid parcel IDs found in case objects');
    }

    const mpParcelIds = caseObjects.filter(isMasterPetitionCase).map((c) => c.parcel_id).filter(Boolean);

    // Validate settlement values - ensure positive finite AV values
    for (const caseObj of caseObjects) {
      const settlementValue = caseObj.settlementValue;
      const n = Number(settlementValue);
      if (
        settlementValue === null ||
        settlementValue === undefined ||
        !Number.isFinite(n) ||
        n <= 0
      ) {
        throw new Error(`Settlement validation failed: AV value is zero, blank, or invalid for parcel ${caseObj.parcel_id}. Settlement value: ${settlementValue}. Please enter a settlement amount greater than zero.`);
      }
    }

    // Validate original assessment (AV) exists for each case; throw and notify if missing/zero
    // we had erroneous ST determinations when it shouldn't have been possible, put this in as a protection.
    const missingAssessmentCases = caseObjects.filter((caseObj) => {
      const av = getAVUtil(caseObj);
      return av == null || av === 0;
    });
    if (missingAssessmentCases.length > 0) {
      const subject = 'Settlement validation: missing or zero original assessment';
      const caseDetails = missingAssessmentCases.map((c) => ({
        parcel_id: c.parcel_id,
        Address: c.Address ?? c.address,
        PropertyAssessment: c.PropertyAssessment,
        AssessmentVillage: c.AssessmentVillage,
        AssessmentTown: c.AssessmentTown,
        settlementValue: c.settlementValue
      }));
      const body = [
        `Settlement was blocked because one or more cases had missing or zero assessment value (AV).`,
        `Context: muniCode=${context.muniCode}, taxYear=${context.taxYear}, userId=${context.userId}, caseType=${context.caseType || (context.villageRun === 1 ? 'Village' : 'Town')}.`,
        `Affected parcels (${missingAssessmentCases.length}):`,
        JSON.stringify(caseDetails, null, 2)
      ].join('\n\n');
      axiosInstance.post('/send_internal_email', {
        toAddress: ['jrburns@aventine.ai'],
        subject,
        message: body
      }).catch((err) => logger.error('Failed to send missing-assessment notification email:', err));
      const parcelList = missingAssessmentCases.map((c) => c.parcel_id).join(', ');
      const msg = `Settlement failed: no valid assessment value (AV) for parcel(s) ${parcelList}. An alert has been sent to the team.`;
      throw new Error(msg);
    }

    const negotiatorId = await negotiatorIdForSettlement(context);
    
    // Get stage from first case (assuming all cases have same stage)
    const stage = caseObjects[0]?.settlementDetails?.stage || 'SCAR';
    const isArt7Settlement = stage === 'Art7';

    if (stage === 'BAR' && mpParcelIds.length > 0) {
      throw new Error('Art 7 / Master Petition cases cannot be settled at BAR. Select Art 7.');
    }
    if (isArt7Settlement) {
      const notArt7 = caseObjects.filter((c) => !isMasterPetitionCase(c));
      if (notArt7.length > 0) {
        throw new Error('Art 7 settlement is only available for Art 7 filed / Master Petition cases.');
      }
    }

    const currentDate = toMySQLDateTime(new Date());
    
    const stipulation = buildStipulationObject({
      stage,
      villageRun: context.villageRun,
      taxYear: context.taxYear,
    });

    logger.log('🔧 [SETTLEMENT] Created stipulation object:', stipulation);
    
    // Prepare batch courtfiles update data with per-parcel values
    const courtfilesUpdates = {};
    const sdBdParcels = [];
    const sdBdTroubleshootParcels = [];

    for (const caseObject of caseObjects) {
      const originalAssessment = getAVUtil(caseObject);
      const isWithdrawal = caseObject.settlementDetails?.isWithdrawal || false;
      const settlementValueAV = caseObject.settlementValue;
      const determinationValue = settlementValueAV;
      const percentageChange = Math.abs((settlementValueAV - originalAssessment) / originalAssessment);
      const actionCode = determineActionCode(stage, settlementValueAV, originalAssessment, isWithdrawal);

      if (!isWithdrawal && percentageChange < 0.0025) {
        const isProperNoReduction = actionCode === 'SD' || actionCode === 'BD';
        if (!isProperNoReduction) {
          const subject = 'Settlement action determination: no reduction (SD/BD) – troubleshooting';
          const body = [
            `Determination action ${actionCode} was applied because percentage change (${(percentageChange * 100).toFixed(4)}%) is below 0.25% threshold.`,
            `Context: muniCode=${context.muniCode}, taxYear=${context.taxYear}, userId=${context.userId}, caseType=${context.caseType || (context.villageRun === 1 ? 'Village' : 'Town')}, stage=${stage}.`,
            'Case and calculation:',
            JSON.stringify({
              parcel_id: caseObject.parcel_id,
              Address: caseObject.Address ?? caseObject.address,
              originalAssessment,
              settlementValueAV: determinationValue,
              percentageChange,
              percentageChangePct: `${(percentageChange * 100).toFixed(4)}%`,
              actionCode,
              PropertyAssessment: caseObject.PropertyAssessment,
              AssessmentVillage: caseObject.AssessmentVillage,
              AssessmentTown: caseObject.AssessmentTown
            }, null, 2)
          ].join('\n\n');
          axiosInstance.post('/send_internal_email', {
            toAddress: ['jrburns@aventine.ai'],
            subject,
            message: body
          }).catch((err) => logger.error('Failed to send no-reduction determination email:', err));
          sdBdTroubleshootParcels.push({ parcel_id: caseObject.parcel_id, actionCode });
        } else {
          sdBdParcels.push({ parcel_id: caseObject.parcel_id, actionCode });
        }
      }

      // Add to courtfiles updates with per-parcel structure (Courtfiles DB columns only).
      // Art 7 settlements write CommercialCourtfiles Art7* columns instead.
      courtfilesUpdates[caseObject.parcel_id] = isArt7Settlement
        ? {
            Art7DeterminationAction: actionCode,
            Art7DeterminationValue: determinationValue,
            Art7SettleDate: currentDate,
            Art7DeterminationDate: currentDate,
            Art7Negotiatior: negotiatorId,
          }
        : {
            [`${stage}DeterminationAction`]: actionCode,
            [`${stage}DeterminationValue`]: determinationValue,
            [`${stage}SettleDate`]: currentDate,
            [`${context.villageRun === 1 ? 'Village' : ''}WithdrawalReason`]: caseObject.settlementDetails?.withdrawalReason || null,
            Negotiator: negotiatorId,
            ...(stage === 'BAR'
              ? (context.villageRun === 1 ? { VillageSCARFiled: 0 } : { SCARFiled: 0 })
              : {}),
          };
    }

    if (sdBdParcels.length > 0) {
      const actionCode = sdBdParcels[0].actionCode;
      const summary = caseObjects.length === 1
        ? `Settlement applied as ${actionCode} (no reduction) for parcel ${sdBdParcels[0].parcel_id}.`
        : `Settlement applied as ${actionCode} (no reduction) for ${sdBdParcels.length} parcel${sdBdParcels.length === 1 ? '' : 's'}.`;
      // toast.warning(summary, { autoClose: 5000 });
      logger.log('summary:', summary);
    }
    if (sdBdTroubleshootParcels.length > 0) {
      const actionCode = sdBdTroubleshootParcels[0].actionCode;
      const summary = sdBdTroubleshootParcels.length === 1
        ? `Settlement applied as ${actionCode} (no reduction) for parcel ${sdBdTroubleshootParcels[0].parcel_id}. Details sent to team for troubleshooting.`
        : `Settlement applied as ${actionCode} (no reduction) for ${sdBdTroubleshootParcels.length} parcel${sdBdTroubleshootParcels.length === 1 ? '' : 's'}. Details sent to team for troubleshooting.`;
      toast.warning(summary, { autoClose: false });
    }

    const stipInfoKey = stage === 'BAR' ? 'bar' : stage === 'SCAR' ? 'scar' : null;
    const mpParcelIdSet = new Set(mpParcelIds.map((id) => String(id)));
    const clearArt7FiledParcelIds = isArt7Settlement ? [] : mpParcelIds;
    const frontendOnlyCaseUpdates = Object.fromEntries(
      parcelIds.map((id) => {
        const caseObject = caseObjects.find((c) => c.parcel_id === id);
        const priorStipInfo = caseObject?.StipInfo && typeof caseObject.StipInfo === 'object' ? caseObject.StipInfo : {};
        return [
          id,
          {
            ...(stipInfoKey ? {
              StipInfo: {
                ...priorStipInfo,
                [stipInfoKey]: { status: 'Agreed', negotiator: negotiatorId, lastUpdated: currentDate },
              },
            } : {}),
            ...(isArt7Settlement ? { Negotiator: negotiatorId } : {}),
            ...(!isArt7Settlement && mpParcelIdSet.has(String(id)) ? { Art7Filed: 0, MasterPetition: 0 } : {}),
          },
        ];
      })
    );

    logger.log('courtfilesUpdates:', courtfilesUpdates);
    // Await so stip generation / batch offers do not run if Courtfiles update fails.
    let courtfilesResult = { lambda_responses: [] };
    try {
      courtfilesResult = await updateCourtfilesGeneralized({
        muniCode: context.muniCode,
        taxYear: context.taxYear,
        parcelIds: parcelIds,
        fieldsToUpdate: courtfilesUpdates,
        stipulation: stipulation,
        updateFrontend: true,
        frontendOnlyCaseUpdates,
        townView: context.townView,
        clearArt7FiledParcelIds,
      });
      logger.log('✅ [SETTLEMENT] Courtfiles updated successfully');
      logger.log('🔍 [SETTLEMENT] Courtfiles result:', courtfilesResult);
    } catch (courtfilesError) {
      logger.error('Failed to update Courtfiles during settlement:', courtfilesError);
      throw courtfilesError;
    }

    let stipulationResult = null;
    if (!context.townView && !isArt7Settlement) {
      stipulationResult = await generateStipsOnSettlement({ parcelIds, stipulation });
    }
    
    // Create batch offers for all cases
    const batchOffers = [];
    const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
    
    for (const caseObject of caseObjects) {
      const conversionFactor = caseObject.settlementDetails?.conversionFactor || 1;
      const settlementValue = Math.round(caseObject.settlementValue);
      const targetRound = determineSettlementRound(getNegotiationObj, caseObject.parcel_id);
      
      logger.log(`🔄 [SETTLEMENT] Converting AV to MV for negotiations:`, {
        parcelId: caseObject.parcel_id,
        settlementValueAV: caseObject.settlementValue,
        conversionFactor,
        settlementValue
      });
      
      // Add offers for both parties
      batchOffers.push({
        parcelId: caseObject.parcel_id,
        offeredBy: 'Aventine',
        offerValue: Number(settlementValue),
        response: 'Accept',
        liveOffer: 1,
        round: targetRound
      });
      
      batchOffers.push({
        parcelId: caseObject.parcel_id,
        offeredBy: 'Town',
        offerValue: Number(settlementValue),
        response: 'Accept',
        round: targetRound,
      });
    }
    
    // Execute batch offers update
    // Use the round from the first case (all cases should have the same round)
    const targetRound = caseObjects.length > 0 ? determineSettlementRound(getNegotiationObj, caseObjects[0].parcel_id) : null;
    logger.log(`🔄 [SETTLEMENT] Executing batch offers update with round: ${targetRound}`);
    // Negotiation rows: updateOffersBatch → POST /create_negotiation_offers_batch (see realtimeOfferService)
    const offersResult = await updateOffersBatch(batchOffers, { ...context, issuedByNegotiatorId: negotiatorId }, {
      updateFrontend: true,
      round: targetRound,
    });
    
    const caseStatusResult = await completeSettlementPipelineTasks(parcelIds, context, currentDate);
    
    // Lambda invocation is now handled automatically by the backend after database updates
    // No need for separate lambda call from frontend

    if (!isTownViewDevNoPersist(context.townView)) {
      usePersistedDataStore.getState().refreshMunicipalResults?.();
    }
    if (!context.townView) {
      refreshAlgorithmOffers().catch((error) => logger.error('Refresh algo offers after settlement failed:', error));
    }

    // Return result - for single case, return single case format; for batch, return batch format
    if (caseObjects.length === 1) {
      return {
        success: caseStatusResult.success && offersResult.success,
        message: `Settlement processed for case ${parcelIds[0]}`,
        caseId: parcelIds[0],
        settlementValue: caseObjects[0].settlementValue,
        timestamp: new Date().toISOString(),
        data: { 
          caseStatus: caseStatusResult, 
          offers: offersResult,
          stipulation: stipulationResult,
          courtfiles: courtfilesResult
        }
      };
    } else {
      return {
        success: caseStatusResult.success && offersResult.success,
        message: `Batch settlement processed for ${parcelIds.length} cases`,
        caseIds: parcelIds,
        processedCount: parcelIds.length,
        timestamp: new Date().toISOString(),
        data: { 
          caseStatus: caseStatusResult, 
          offers: offersResult,
          stipulation: stipulationResult,
          courtfiles: courtfilesResult
        }
      };
    }
    
  } catch (error) {
    logger.error('Error processing settlement:', error);
    throw error;
  }
};


/**
 * Validates case eligibility for settlement. When opening the popover, call with (caseObject).
 * When submitting a value, call with (caseObject, { settlementValueAV }). If the value is
 * adjusted (min/max clamp), returns eligible: false and adjustedValueAV so the caller can
 * abort and update the input; toast is shown by the util.
 *
 * @param {Object} caseObject - Case row/case object
 * @param {Object} [options] - Optional { settlementValueAV } (AV terms) to validate
 * @returns {{ eligible: boolean, reason: string, adjustedValueAV?: number }}
 */
export const validateSettlementEligibility = (caseObject, options = {}) => {
  if (!caseObject) {
    return { eligible: false, reason: 'No case object provided' };
  }
  if (!caseObject.parcel_id) {
    return { eligible: false, reason: 'Missing parcel ID' };
  }

  if (options.settlementValueAV === undefined) {
    return { eligible: true, reason: 'Case is eligible for settlement' };
  }

  const settlementValueAV = options.settlementValueAV;
  if (settlementValueAV == null || !Number.isFinite(Number(settlementValueAV)) || Number(settlementValueAV) <= 0) {
    return { eligible: false, reason: 'Enter a valid settlement value greater than zero.' };
  }

  const result = validateAndAdjustOfferValue(
    settlementValueAV,
    caseObject,
    true,
    true,
    { settlementStage: options.settlementStage }
  );

  if (result.wasAdjusted) {
    return { eligible: false, reason: 'Settlement value outside allowed range; adjust and try again.', adjustedValueAV: result.adjustedValue };
  }
  return { eligible: true, reason: 'Case is eligible for settlement' };
};

/**
 * Rebuilds the municipality's offer ladder with settled cases as fixed rungs, then patches
 * AlgorithmFirstOffer / AlgorithmSecondOffer / AlgorithmCeilingOffer on the open cases in negotiationObj.
 * Also stores the current-year achieved reduction on settled cases (same card math as muni results) as settledPerformance.
 * @returns {Promise<number>} Count of cases whose ladder was refreshed
 */
export const refreshAlgorithmOffers = async () => {
  const { negotiationObj, setNegotiationObj, setNegotiationObjVersion, setSettledPerformance, setSettledPerformanceLoading } = usePersistedDataStore.getState();
  logger.log('🔄 [SETTLEMENT] Refreshing algorithmic offers');
  setSettledPerformanceLoading(true);
  try {
    const { data } = await axiosInstance.post('/refresh_algorithmic_offers', {
      MuniCode: negotiationObj.MuniCode,
      TaxYear: negotiationObj.TaxYear,
      WorkupType: negotiationObj.reportType === 'assessment' ? 'assessment' : 'sales',
    });
    const offers = data.offers || {};
    logger.log('🔄 [SETTLEMENT] Offers refreshed values are:', offers);
    const latest = usePersistedDataStore.getState().negotiationObj;
    console.log('latest:', latest);
    if (data.performance) setSettledPerformance(data.performance);
    setNegotiationObj({
      ...latest,
      cases: latest.cases.map((caseItem) => (offers[caseItem.parcel_id] ? { ...caseItem, ...offers[caseItem.parcel_id] } : caseItem)),
    });
    setNegotiationObjVersion((v) => v + 1);
    return Object.keys(offers).length;
  } finally {
    setSettledPerformanceLoading(false);
  }
};

/**
 * Determine the appropriate round for inserting settlement offers, mirroring realtime updater logic
 * - If no negotiations: round 1
 * - If current round already has 2 offers: next round
 * - Else: current round
 */
const determineSettlementRound = (negotiationObj, parcelId) => {
  const caseData = findNegotiationCaseByParcelId(negotiationObj, parcelId);
  if (!caseData || !Array.isArray(caseData.Negotiations) || caseData.Negotiations.length === 0) {
    return 1;
  }
  const currentRound = Math.max(...caseData.Negotiations.map((r) => r.round || 0));
  const currentRoundData = caseData.Negotiations.find((r) => r.round === currentRound);
  const offersCount = currentRoundData?.offers?.length || 0;
  if (offersCount >= 2) {
    return currentRound + 1;
  }
  return currentRound;
};


/**
 * Create settlement offers using batch update service
 * @param {Object} caseObject - The case object containing case details
 * @param {Object} options - Options containing settlementValue and context
 * @returns {Promise<Object>} Settlement result
 */
export const createSettlementOffersBatch = async (caseObject, options = {}) => {
  const { settlementValue, context } = options;
  if (settlementValue === undefined || settlementValue === null) {
    throw new Error('Settlement value is required');
  }

  if (!context || !context.muniCode || !context.taxYear || !context.userId) {
    throw new Error('Missing required context: muniCode, taxYear, or userId');
  }

  // Compute target round from current state
  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  const targetRound = determineSettlementRound(getNegotiationObj, caseObject.parcel_id);
  logger.log('🔍 [SETTLEMENT] Target round:', targetRound);

  // Prepare offers for each party separately
  const aventineOffer = {
    parcelId: caseObject.parcel_id,
    offeredBy: 'Aventine',
    offerValue: Number(settlementValue),
    response: 'Accept',
  };

  const townOffer = {
    parcelId: caseObject.parcel_id,
    offeredBy: 'Town',
    offerValue: Number(settlementValue),
    response: 'Accept',
  };

  const currentDate = toMySQLDateTime(new Date());
  const caseStatusResult = await completeSettlementPipelineTasks(
    [caseObject.parcel_id],
    context,
    currentDate,
  );

  // Make separate batch calls (Aventine first, then Town)
  const aventineResult = await updateOffersBatch([aventineOffer], context, {
    round: targetRound,
    liveOffer: 1,
    updateFrontend: true,
  });

  const townResult = await updateOffersBatch([townOffer], context, {
    round: targetRound,
    liveOffer: 1,
    updateFrontend: true,
  });

  // Aggregate results
  const result = {
    success: caseStatusResult.success && aventineResult.success && townResult.success,
    data: { caseStatus: caseStatusResult, aventine: aventineResult, town: townResult },
  };

  if (result.success) {
    return {
      success: true,
      message: `Settlement offers created for case ${caseObject.parcel_id} in round ${targetRound}`,
      caseId: caseObject.parcel_id,
      settlementValue,
      round: targetRound,
      timestamp: new Date().toISOString(),
      data: result.data,
    };
  } else {
    throw new Error(result.error || 'Failed to create settlement offers');
  }
};
